/**
 * Marketplace -- cross-tenant loan-participation browse/listing page (LPM-1).
 *
 * Redesigned on top of the LPM-0 network projection: this page now calls
 * GET /api/v1/syndication/marketplace (the masked, audited, cross-tenant
 * view across every enableLoanParticipations tenant) instead of the
 * tenant-scoped GET /offerings. Facet counts (status / regulatory framework
 * / rate type / loan type / amount band) come from the server-side
 * GET /marketplace/facets aggregate. Per-row eligibility (LPM-2) is computed
 * server-side against the caller's institution regulatory profile and
 * rendered as a chip; "Hide ineligible" filters INELIGIBLE rows server-side
 * (an UNKNOWN verdict -- profile not yet configured -- is never excluded, and
 * a WARN verdict renders "Review"). The toggle read "Eligible only" until the
 * 2026-09-27 sweep (fi-g9-5), which is not what the filter does.
 *
 * Facet filters (loan-syndication-protocol 1.2.0, GH #4669): every dimension
 * the facets aggregate counts -- the 1.1 five plus rate, term, LTV,
 * remaining capacity, collateral class, deadline window, waterfall,
 * geography, lead charter, FICO, DSCR and settlement rail -- is sent to
 * GET /marketplace as a browse parameter (marketplace-facets.ts), so the
 * server filters the whole network before it paginates and a chosen facet
 * matches the count beside it. The free-text search still narrows the
 * fetched page client-side: the contract carries no search parameter.
 *
 * Counterparty masking is unchanged (server-side, C14 C-3): the SPA never
 * attempts to unmask, it renders whatever shape the server returns.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Inbox, Columns3 } from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  Button,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Skeleton,
  Card,
  Badge,
  EmptyState,
  Sheet,
  TooltipCard,
  Switch,
  Input,
} from '@aaimplatform/ui';
import type {
  ColumnDef,
  ActiveFilter,
  RowSelectionState,
} from '@aaimplatform/ui';
import { useQuery } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import {
  ELIGIBILITY_BADGE_VARIANT,
  ELIGIBILITY_LABEL,
  ELIGIBILITY_SEVERITY_LABEL,
  ELIGIBILITY_SHORT_LABEL,
  ELIGIBILITY_TONE,
} from './eligibility';
import { DealAlertsPanel } from './DealAlertsPanel';
import { FeeScheduleDisclosure } from './FeeScheduleDisclosure';
import {
  formatPercent,
  formatRatioPercent,
  formatCurrencyGrid as formatCurrencyFull,
  formatCurrencyCompact as formatCurrency,
  formatDateShort,
  daysUntil,
  isPastCommitmentDeadline,
  formatLeadDisplay,
  formatAssetType,
  formatCommitted,
  LOAN_TYPE_LABELS,
  OFFERING_STATUS_LABELS,
} from './format';
import { toSyndicationReadError } from './read-error';
import { ReadErrorBanner } from './ReadErrorBanner';
import {
  REGULATORY_FRAMEWORK_LABELS,
  buildFacetFilterDefinitions,
  facetQueryFromFilters,
} from './marketplace-facets';
import type { MarketplaceFacetQuery } from './marketplace-facets';

type ParticipationOffering = LspComponents['schemas']['ParticipationOffering'];
type OfferingStatus = LspComponents['schemas']['OfferingStatus'];
/** GET /marketplace and /marketplace/facets accept only the two browseable statuses. */
type MarketplaceStatus = Extract<OfferingStatus, 'OPEN' | 'FULLY_COMMITTED'>;
type MarketplaceFacets = LspComponents['schemas']['MarketplaceFacets'];

/** Hover text for a listing that carries no eligibility verdict. */
const NOT_ASSESSED_EXPLANATION =
  "No eligibility verdict: listings your own institution leads are not assessed against its profile.";

/**
 * Terminal-density short codes for the browse grid's deal meta line. The
 * full labels stay on the facet filter, deal brief, and compare surfaces --
 * a repeating chip column of full labels cost ~130px of grid width for a
 * value that is per-deal metadata, not a scan column (2026-07-02 QA).
 */
const REGULATORY_FRAMEWORK_SHORT: Record<string, string> = {
  NCUA_701_22: 'NCUA',
  OCC_12_CFR_32: 'OCC',
  FDIC: 'FDIC',
  STATE: 'State',
  OTHER: 'Other',
};

/**
 * A listing's title: its deal name, or a descriptor built from the listing
 * itself when the lead gave none. The lead's name stood in for a missing deal
 * name, so the caller's three unnamed listings all read 'OICU Credit Union'
 * and differed only by amount (r3 fi-g9-5). The lead stays on the meta line.
 */
function listingTitle(o: ParticipationOffering): string {
  return o.dealName ?? `${LOAN_TYPE_LABELS[o.loanDetails.loanType]} \u00b7 ${o.offeringId.slice(0, 8)}`;
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

function useMarketplace(params: {
  status?: MarketplaceStatus;
  eligibleOnly: boolean;
  facets: MarketplaceFacetQuery;
}) {
  return useQuery({
    queryKey: ['lsp-marketplace', params.status ?? 'ALL', params.eligibleOnly, params.facets],
    queryFn: async () => {
      const { data, error, response } = await loanSyndicationClient.GET(
        '/api/v1/syndication/marketplace',
        {
          params: {
            query: {
              status: params.status,
              eligibleOnly: params.eligibleOnly,
              ...params.facets,
              page: 1,
              pageSize: 100,
            },
          },
        },
      );
      // Read before the error narrowing: on a route whose contract declares
      // no error body, openapi-fetch types that branch as never.
      const httpStatus = response.status;
      if (error) {
        throw toSyndicationReadError(error, httpStatus, 'Failed to load the marketplace');
      }
      return data;
    },
    staleTime: 30 * 1000,
    retry: false,
  });
}

function useMarketplaceFacets(params: { status?: MarketplaceStatus }) {
  return useQuery({
    queryKey: ['lsp-marketplace-facets', params.status ?? 'ALL'],
    queryFn: async () => {
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/marketplace/facets',
        { params: { query: { status: params.status } } },
      );
      if (error) {
        throw new Error((error as { message?: string })?.message ?? 'Failed to load facet counts');
      }
      return data as MarketplaceFacets;
    },
    staleTime: 30 * 1000,
    retry: false,
  });
}

function useListingEconomics(offeringId: string | undefined, notional: number) {
  return useQuery({
    queryKey: ['lsp-listing-economics', offeringId, notional],
    queryFn: async () => {
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/marketplace/listings/{offeringId}/economics',
        {
          params: {
            path: { offeringId: offeringId! },
            query: {
              notional,
              settlementDate: new Date().toISOString().slice(0, 10),
            },
          },
        },
      );
      if (error) {
        throw new Error((error as { message?: string })?.message ?? 'Failed to compute economics');
      }
      return data;
    },
    enabled: !!offeringId && notional > 0,
    staleTime: 10 * 1000,
    retry: false,
  });
}

// useFeeSchedule + the pre-commit fee disclosure block moved to
// FeeScheduleDisclosure.tsx (LPM-8) -- one implementation, consumed by this
// drawer AND the OfferingDetail IOI dialog.

// ---------------------------------------------------------------------------
// Deal Brief drawer -- economics + fees quick-look
// ---------------------------------------------------------------------------

function DealBriefDrawer({
  offering,
  onClose,
  isCompared,
  onToggleCompare,
}: {
  offering: ParticipationOffering | null;
  onClose: () => void;
  isCompared: boolean;
  onToggleCompare: (o: ParticipationOffering) => void;
}) {
  const navigate = useNavigate();
  const [notional, setNotional] = useState<number>(offering?.participationStructure.minimumCommitment ?? 0);
  const economics = useListingEconomics(offering?.offeringId, notional);

  if (!offering) return null;

  const isOpen = offering.status === 'OPEN';
  const isIneligible = offering.eligibility?.verdict === 'INELIGIBLE';
  // No "Review allocations" action here until the marketplace response says
  // which listings the caller leads. The earlier check compared the user's
  // tenantId with leadParticipantId, a CrtParticipant id, so it never matched
  // and the action never rendered (fi-g9-12). An ownership signal from the
  // server is a contract change, recorded for the operator.

  return (
    <Sheet
      open={!!offering}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      side="right"
      size="lg"
      title={listingTitle(offering)}
      // Lead, asset type and framework in words, joined with a middle dot:
      // the subtitle printed REAL_ESTATE and PLEDGED_ASSET between ASCII
      // double hyphens (r3 fi-g9-7).
      description={[
        formatLeadDisplay(offering),
        formatAssetType(offering),
        REGULATORY_FRAMEWORK_LABELS[offering.regulatoryFramework ?? 'OTHER'],
      ].join(' \u00b7 ')}
    >
      <div className="flex flex-col gap-6 p-1">
        {/* Full deal terms: everything the browse row carries (and the fields
            the terminal-density grid folded away), at full precision. */}
        <div>
          <div className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            Deal terms
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {([
              ['Status', OFFERING_STATUS_LABELS[offering.status]],
              ['Framework', REGULATORY_FRAMEWORK_LABELS[offering.regulatoryFramework ?? 'OTHER']],
              ['Loan amount', formatCurrencyFull(offering.loanDetails.loanAmount)],
              ['Rate', `${formatPercent(offering.loanDetails.interestRate)} ${offering.loanDetails.rateType === 'VARIABLE' ? 'variable' : 'fixed'}`],
              ['Term', `${offering.loanDetails.termMonths} months`],
              ['LTV', formatRatioPercent(offering.collateralSummary?.ltvRatio)],
              ['Collateral value', formatCurrencyFull(offering.collateralSummary?.totalValue)],
              ['Collateral assets', offering.collateralSummary ? `${offering.collateralSummary.assetCount} (${formatAssetType(offering)})` : '--'],
              ['Syndication total', formatCurrencyFull(offering.participationStructure.totalSyndicationAmount)],
              ['Lead retained', formatCurrencyFull(offering.participationStructure.leadRetainedAmount)],
              ['Min commitment', formatCurrencyFull(offering.participationStructure.minimumCommitment)],
              ['Remaining', formatCurrencyFull(offering.remainingCapacity)],
              // Distinct participants when the response counts them, else
              // commitment rows, each in its own unit; an absent count reads
              // as not reported, never as a measured 0 (r3 fi-g9-6).
              ['Committed', formatCommitted(offering) ?? 'Not reported'],
              ['Deadline', `${formatDateShort(offering.commitmentDeadline)}${(() => { if (isPastCommitmentDeadline(offering)) return ' (passed)'; const d = daysUntil(offering.commitmentDeadline); return d !== null && d >= 0 && d <= 7 ? ` (${d}d left)` : ''; })()}`],
              ['Distribution', offering.participationStructure.distributionWaterfall === 'PRO_RATA' ? 'Pro rata' : 'Sequential'],
            ] as Array<[string, string]>).map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-3 border-b border-recessed/60 pb-1">
                <dt className="font-sans text-xs text-tertiary">{k}</dt>
                <dd className="font-sans text-xs font-medium text-primary tabular-nums text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        {offering.eligibility && (
          <div>
            <div className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
              Eligibility
            </div>
            <Badge variant={ELIGIBILITY_BADGE_VARIANT[offering.eligibility.verdict]}>
              {ELIGIBILITY_LABEL[offering.eligibility.verdict]}
            </Badge>
            <ul className="mt-2 space-y-1">
              {offering.eligibility.reasons.map((r, i) => (
                <li key={i} className="font-sans text-xs text-secondary">
                  <span className="font-semibold">{ELIGIBILITY_SEVERITY_LABEL[r.severity]}:</span> {r.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            Economics calculator
          </div>
          <Input
            label="Notional"
            type="text"
            inputMode="numeric"
            value={notional ? notional.toLocaleString('en-US') : ''}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              setNotional(digits ? Number(digits) : 0);
            }}
          />
          {economics.isLoading ? (
            <Skeleton className="mt-3 h-24 w-full" />
          ) : economics.data ? (
            <dl className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <dt className="font-sans text-xs text-tertiary">Clean consideration</dt>
                <dd className="font-mono text-sm tabular-nums text-primary">
                  {formatCurrencyFull(economics.data.cleanConsideration)}
                </dd>
              </div>
              <div>
                <dt className="font-sans text-xs text-tertiary">Accrued interest</dt>
                <dd className="font-mono text-sm tabular-nums text-primary">
                  {formatCurrencyFull(economics.data.accrued)}
                </dd>
              </div>
              <div>
                <dt className="font-sans text-xs text-tertiary">Settlement amount</dt>
                <dd className="font-mono text-sm tabular-nums font-semibold text-primary">
                  {formatCurrencyFull(economics.data.settlementAmount)}
                </dd>
              </div>
              <div>
                <dt className="font-sans text-xs text-tertiary">Net coupon to buyer</dt>
                <dd className="font-mono text-sm tabular-nums text-primary">
                  {formatPercent(economics.data.netCouponToBuyer)}
                </dd>
              </div>
            </dl>
          ) : null}
        </div>

        <FeeScheduleDisclosure arrangedAmount={offering.participationStructure.totalSyndicationAmount} />

        {/* Action rail: the browse grid carries one Brief button per row
            (terminal density); the transactional CTAs live here with full
            eligibility context on screen. */}
        <div className="flex items-center justify-between gap-3 border-t border-recessed pt-4">
          <div className="flex items-center gap-2">
            <Button
              variant={isOpen && !isIneligible ? 'primary' : 'secondary'}
              size="sm"
              icon={ExternalLink}
              disabled={isIneligible}
              onClick={() => navigate(`/syndication/offerings/${offering.offeringId}`)}
            >
              {isIneligible ? 'Ineligible' : isOpen ? 'Indicate Interest' : 'View details'}
            </Button>
          </div>
          <Switch
            isSelected={isCompared}
            onChange={() => onToggleCompare(offering)}
            label="Compare"
            size="sm"
          />
        </div>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Marketplace page
// ---------------------------------------------------------------------------

export function Marketplace() {
  const navigate = useNavigate();
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [search, setSearch] = useState('');
  const [eligibleOnly, setEligibleOnly] = useState(true);
  const [detailOffering, setDetailOffering] = useState<ParticipationOffering | null>(null);
  // Compare set: CONTROLLED DataGrid selection keyed by offeringId, so the
  // row checkboxes and the deal-brief drawer's Compare toggle write the
  // same state (2026-07-02 QA: the drawer is the browse hub and must be
  // able to stage a deal for comparison).
  const [compareSelection, setCompareSelection] = useState<RowSelectionState>({});

  const statusFilter = activeFilters.find((f) => f.filterId === 'status')?.value as MarketplaceStatus | undefined;
  const facetQuery = useMemo(() => facetQueryFromFilters(activeFilters), [activeFilters]);

  const { data, isLoading, error } = useMarketplace({ status: statusFilter, eligibleOnly, facets: facetQuery });
  const { data: facetsData } = useMarketplaceFacets({ status: statusFilter });

  const offerings = data?.offerings ?? [];
  const selectedOfferings = useMemo(
    () => offerings.filter((o) => compareSelection[o.offeringId]),
    [offerings, compareSelection],
  );
  const toggleCompare = (o: ParticipationOffering) =>
    setCompareSelection((prev) => {
      const next = { ...prev };
      if (next[o.offeringId]) delete next[o.offeringId];
      else next[o.offeringId] = true;
      return next;
    });

  // Facet filters are applied by the server (facetQuery, above). The search
  // box is the one client-side narrowing: it reads the fetched page only.
  const filteredOfferings = useMemo(() => {
    let rows = offerings;
    if (search.trim()) {
      const query = search.toLowerCase();
      rows = rows.filter((o) => {
        const dealName = (o.dealName ?? '').toLowerCase();
        const lead = formatLeadDisplay(o).toLowerCase();
        const assetClass = formatAssetType(o).toLowerCase();
        const framework = REGULATORY_FRAMEWORK_LABELS[o.regulatoryFramework ?? 'OTHER'].toLowerCase();
        return (
          dealName.includes(query)
          || lead.includes(query)
          || assetClass.includes(query)
          || framework.includes(query)
          || o.offeringId.toLowerCase().includes(query)
        );
      });
    }
    return rows;
  }, [offerings, search]);

  // Offerings whose commitment deadline has passed are still listed while
  // the server holds them OPEN (fi-g9-3), so the grid labels them and the
  // headline figures leave them out: capacity nobody can commit to by the
  // listing's own terms is not available capacity.
  const openOfferings = useMemo(
    () => filteredOfferings.filter((o) => !isPastCommitmentDeadline(o)),
    [filteredOfferings],
  );
  const pastDeadlineCount = filteredOfferings.length - openOfferings.length;
  const pastDeadlineNote = pastDeadlineCount > 0
    ? `; ${pastDeadlineCount} past deadline not counted`
    : '';
  // remainingCapacity is required by the ParticipationOffering contract.
  const totalRemaining = useMemo(
    () => openOfferings.reduce((sum, o) => sum + o.remainingCapacity, 0),
    [openOfferings],
  );
  // ELIGIBLE only. A WARN verdict renders "Review" in the grid and is counted
  // separately, so the tile never calls a Review row eligible (fi-g9-5).
  const eligibleCount = useMemo(
    () => openOfferings.filter((o) => o.eligibility?.verdict === 'ELIGIBLE').length,
    [openOfferings],
  );
  const reviewCount = useMemo(
    () => openOfferings.filter((o) => o.eligibility?.verdict === 'WARN').length,
    [openOfferings],
  );
  const closingThisWeekCount = useMemo(
    () => filteredOfferings.filter((o) => {
      const d = daysUntil(o.commitmentDeadline);
      return d !== null && d >= 0 && d <= 7;
    }).length,
    [filteredOfferings],
  );
  // The unweighted mean of the gross loan coupons. It was labeled 'Network
  // Yield', a word the Brief drawer uses for the buyer's net coupon and
  // Positions uses for a commitment-weighted annualized yield (r3 fi-g9-3).
  const averageCoupon = useMemo(() => {
    if (openOfferings.length === 0) return null;
    const sum = openOfferings.reduce((acc, o) => acc + o.loanDetails.interestRate, 0);
    return sum / openOfferings.length;
  }, [openOfferings]);

  const filterDefinitions = useMemo(() => buildFacetFilterDefinitions(facetsData), [facetsData]);

  const columns = useMemo<ColumnDef<ParticipationOffering, unknown>[]>(
    () => [
      {
        id: 'eligibility',
        header: 'Eligibility',
        cell: ({ row }) => {
          const e = row.original.eligibility;
          if (!e) {
            return (
              <TooltipCard title="Not assessed" content={NOT_ASSESSED_EXPLANATION}>
                <span className="font-sans text-xs text-tertiary">Not assessed</span>
              </TooltipCard>
            );
          }
          const hardReasons = e.reasons.filter((r) => r.severity !== 'INFO');
          return (
            <TooltipCard
              title={ELIGIBILITY_LABEL[e.verdict]}
              content={
                hardReasons.length > 0
                  ? hardReasons.map((r) => r.message).join(' ')
                  : 'No concentration or regulatory concerns for your institution.'
              }
              variant={e.verdict === 'INELIGIBLE' ? 'accent' : e.verdict === 'WARN' ? 'warm' : 'default'}
            >
              <span className={`inline-flex items-center gap-1.5 font-sans text-xs font-medium ${ELIGIBILITY_TONE[e.verdict]}`}>
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
                {ELIGIBILITY_SHORT_LABEL[e.verdict]}
              </span>
            </TooltipCard>
          );
        },
        // Wide enough for the spelled-out header at laptop widths under the
        // fixed layout's proportional sizing.
        size: 128,
      },
      {
        accessorKey: 'leadInstitutionName',
        header: 'Deal / lead arranger',
        cell: ({ row }) => {
          const o = row.original;
          // Deal name (lead-authored public descriptor) is the primary line
          // when present; the lead identity (masked or disclosed) drops to
          // the meta line, which also carries the framework short-code and
          // the non-default lifecycle state -- per-deal metadata reads with
          // the deal, not as dedicated scan columns (2026-07-02 QA: the
          // Framework and Status chip columns cost 240px of repeating
          // values and pushed the grid into horizontal scroll).
          // An unnamed listing takes a descriptor of its own for the primary
          // line, and the lead (masked or disclosed) stays on the meta line
          // as for a named deal (r3 fi-g9-5). A masked lead reads
          // 'Undisclosed lead arranger'; the caller's own listing reads its
          // own name, never a masking note (fi-g9-4).
          const primary = listingTitle(o);
          const lead = formatLeadDisplay(o);
          const meta = [
            lead,
            REGULATORY_FRAMEWORK_SHORT[o.regulatoryFramework ?? 'OTHER'],
          ].filter(Boolean).join(' \u00b7 ');
          return (
            <div className="min-w-0">
              <div className="truncate text-sm font-sans font-medium text-primary leading-normal">
                {primary}
              </div>
              <div className="truncate text-xs font-sans text-tertiary leading-normal">
                {meta}
                {o.status !== 'OPEN' && (
                  <span className="ml-1.5 font-semibold uppercase tracking-wide text-secondary">
                    {o.status === 'FULLY_COMMITTED' ? 'Full' : o.status.toLowerCase()}
                  </span>
                )}
              </div>
            </div>
          );
        },
        // Large base width: under fixed layout browsers distribute surplus
        // width proportionally across ALL columns, so the deal column's
        // share must dominate by declaration -- ~45% of the base sum keeps
        // deal names readable at 1366 and generous at 1920, truncating
        // instead of forcing horizontal scroll.
        size: 524,
      },
      {
        accessorKey: 'loanDetails',
        header: 'Amount',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="text-right text-sm font-sans font-medium text-primary tabular-nums leading-normal">
            {formatCurrency(row.original.loanDetails.loanAmount)}
          </div>
        ),
        size: 92,
      },
      {
        id: 'rate',
        header: 'Rate / term',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="text-right">
            <div className="text-sm font-sans font-medium text-primary tabular-nums leading-normal">
              {formatPercent(row.original.loanDetails.interestRate)}
              <span className="ml-1 text-xs font-normal text-tertiary">{row.original.loanDetails.rateType === 'VARIABLE' ? 'var' : 'fix'}</span>
            </div>
            <div className="text-xs font-sans text-tertiary tabular-nums leading-normal">
              {row.original.loanDetails.termMonths} mo
            </div>
          </div>
        ),
        size: 104,
      },
      {
        id: 'ltv',
        header: 'LTV / coll',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="text-right">
            <div className="text-sm font-sans font-medium text-primary tabular-nums leading-normal">
              {formatRatioPercent(row.original.collateralSummary?.ltvRatio)}
            </div>
            {row.original.collateralSummary && (
              <div className="text-xs font-sans text-tertiary tabular-nums leading-normal">
                {formatCurrency(row.original.collateralSummary.totalValue)}
              </div>
            )}
          </div>
        ),
        size: 96,
      },
      {
        accessorKey: 'remainingCapacity',
        header: 'Remaining',
        meta: { align: 'right' },
        cell: ({ row }) => {
          // Distinct participants per the spec, or commitment rows when that
          // is all the response carries, each named in its own unit; the line
          // read '{n} committed' for either (r3 fi-g9-6).
          const count = row.original.counterpartyCount ?? row.original.commitmentCount;
          const committed = count !== undefined && count > 0 ? formatCommitted(row.original) : null;
          return (
            <div className="text-right">
              <div className="text-sm font-sans font-medium text-primary tabular-nums leading-normal">
                {formatCurrency(row.original.remainingCapacity)}
              </div>
              {committed && (
                <div className="text-xs font-sans text-tertiary tabular-nums leading-normal">
                  {committed}
                </div>
              )}
            </div>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'commitmentDeadline',
        header: 'Deadline',
        meta: { align: 'right' },
        cell: ({ row }) => {
          const d = daysUntil(row.original.commitmentDeadline);
          const passed = isPastCommitmentDeadline(row.original);
          return (
            <div className="text-right">
              <div className="text-sm font-sans text-secondary tabular-nums leading-normal">
                {formatDateShort(row.original.commitmentDeadline)}
              </div>
              {passed ? (
                <div className="text-xs font-sans font-medium text-tertiary leading-normal">Deadline passed</div>
              ) : d !== null && d >= 0 && d <= 7 && (
                <div className="text-xs font-sans font-medium text-warning tabular-nums leading-normal">{d}d left</div>
              )}
            </div>
          );
        },
        size: 104,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          // One action per row (terminal density): the brief drawer is the
          // hub, and it carries the Indicate Interest / View / allocations
          // CTAs contextually -- three text buttons per row cost 320px and
          // forced the grid into horizontal scroll (2026-07-02 QA).
          <Button variant="tertiary" size="sm" onClick={() => setDetailOffering(row.original)}>
            Brief
          </Button>
        ),
        size: 72,
      },
    ],
    [],
  );

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant H1 -- the pattern the
            rest of the Loan Participations group uses (fi-g9-9). */}
        <p className="text-sm font-sans text-tertiary">
          Browse participation interests listed across the Aaim network,
          your institution's own listings included. Counterparty identities
          are masked unless the lead arranger has elected disclosure.
        </p>
        <div className="flex flex-shrink-0 items-center gap-3">
          <Switch
            isSelected={eligibleOnly}
            onChange={setEligibleOnly}
            label="Hide ineligible"
            size="sm"
          />
          {/* LPM-2: the lead's create path. The wizard prefills from the
              originated Loan record and links the offering via loanId. */}
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/syndication/list-a-loan')}
          >
            List a Loan
          </Button>
        </div>
      </div>

      {error && (
        <ReadErrorBanner
          error={error}
          subject="the marketplace"
          surface="Loan Participations -- Marketplace"
        />
      )}

      <DealAlertsPanel />

      <MetricCardGrid className="mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : error ? (
          <>
            <MetricCard label="Eligible Offerings" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Total Available Capacity" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Average Coupon" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Closing This Week" value="--" caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard
              label="Eligible Offerings"
              value={eligibleCount.toString()}
              // States the same past-deadline exclusion as the next two tiles
              // (r3 fi-g9-4).
              change={`${reviewCount} to review; ${filteredOfferings.length} visible${pastDeadlineNote}`}
              changeType="neutral"
            />
            <MetricCard
              label="Total Available Capacity"
              value={formatCurrency(totalRemaining)}
              change={`Remaining across open offerings${pastDeadlineNote}`}
              changeType="neutral"
            />
            <MetricCard
              label="Average Coupon"
              value={formatPercent(averageCoupon)}
              change={`Unweighted mean loan rate across open offerings${pastDeadlineNote}`}
              changeType="neutral"
            />
            <MetricCard
              label="Closing This Week"
              value={closingThisWeekCount.toString()}
              change="Commitment deadline within 7 days"
              changeType="neutral"
            />
          </>
        )}
      </MetricCardGrid>

      <FilterBar
        filters={filterDefinitions}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={search}
        onSearchChange={setSearch}
        // Fits the field without clipping; the search also reads asset class
        // and framework (fi-g9-14).
        searchPlaceholder="Search deals, arrangers, offering ID"
        className="rounded-t-sm"
      />

      {isLoading ? (
        <Card className="p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </Card>
      ) : error ? null : filteredOfferings.length === 0 ? (
        <Card className="rounded-t-none">
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="No listings match your filters"
            description={
              eligibleOnly
                ? 'Try turning off "Hide ineligible" to see the full network, or widen your other filters.'
                : 'Try widening the deal-size range or clearing a filter.'
            }
          />
        </Card>
      ) : (
        <>
          {/* Compare bar -- appears once rows are staged. Selection is the
              page-owned compareSelection (grid checkboxes + drawer toggle both
              write it); the bar gates navigation to 2-4 deals, the compare
              endpoint's contract. */}
          {selectedOfferings.length > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-surface border border-b-0 border-recessed">
              <span className="text-sm font-sans text-secondary tabular-nums">
                {/* Two sentences, not an ASCII ' -- ' join. */}
                {selectedOfferings.length} deal{selectedOfferings.length === 1 ? '' : 's'} selected
                {selectedOfferings.length < 2 && '. Select at least 2 to compare.'}
                {selectedOfferings.length > 4 && '. Comparison is limited to 4.'}
              </span>
              <Button
                variant="primary"
                size="sm"
                icon={Columns3}
                disabled={selectedOfferings.length < 2 || selectedOfferings.length > 4}
                onClick={() =>
                  navigate(
                    `/syndication/compare?ids=${selectedOfferings.map((o) => o.offeringId).join(',')}`,
                  )
                }
              >
                Compare deals
              </Button>
            </div>
          )}
          {/* The fixed layout sizes columns as percentages of the container, so
              at phone width nine columns squeezed into 358px and overprinted
              (fi-g9-6). minTableWidth keeps the table legible and lets it
              scroll inside the grid's own overflow container below that width;
              laptop content widths (1058px and up) never reach it. */}
          <DataGrid<ParticipationOffering>
            data={filteredOfferings}
            columns={columns}
            enablePagination
            defaultPageSize={20}
            fixedLayout
            minTableWidth={960}
            // No column resizing: with it, DataGrid pins the table to the sum
            // of declared widths (minWidth: getCenterTotalSize) and the grid
            // scrolls horizontally at laptop widths -- the one thing this
            // surface must never do. Fluid w-full + size hints lets the deal
            // column absorb the slack and truncate.
            enableRowSelection
            // Comparison takes 2 to 4 deals, so there is no select-all, and
            // each checkbox is named for the listing it stages rather than
            // its offering UUID (r3 fi-g9-8).
            hideSelectAll
            rowSelectionLabel={(o) => `Compare ${listingTitle(o)}`}
            controlledRowSelection={compareSelection}
            onControlledRowSelectionChange={setCompareSelection}
            onRowClick={(row) => setDetailOffering(row)}
            hoverable
            density="compact"
            getRowId={(row) => row.offeringId}
            className="w-full rounded-b-sm shadow-sm"
          />
        </>
      )}

      {/* Keyed by offeringId so the drawer REMOUNTS per offering: without
          the key, the notional useState initialized from deal A's minimum
          commitment survives into deal B, and the economics calculator
          silently prices deal B at deal A's edited notional. */}
      <DealBriefDrawer
        key={detailOffering?.offeringId ?? 'closed'}
        offering={detailOffering}
        onClose={() => setDetailOffering(null)}
        isCompared={!!detailOffering && !!compareSelection[detailOffering.offeringId]}
        onToggleCompare={toggleCompare}
      />
    </div>
  );
}
